import { z } from 'zod';
import type { InputState, GhostSnapshot } from '../sim/types';
import type { Snapshot } from '../sim/snapshot';
import type { SimEvent } from '../sim/events';
import { HERO_IDS, type HeroId } from '../content/heroes';
import type { RealmPhase } from '../sim/realm';
import type { DuelPhase } from '../sim/duel';
import type { BuildOp, SaveOp } from '../sim/build';

export const PROTOCOL_VERSION = 1;

export const InputSchema = z.object({
  mx: z.number().min(-1).max(1),
  my: z.number().min(-1).max(1),
  ax: z.number().finite(),
  ay: z.number().finite(),
  fire: z.boolean(),
  skills: z.number().int().min(0).max(15),
  dash: z.boolean(),
  use: z.number().int().min(-1).max(3),
  pick: z.number().int().min(-1).max(4),
  interact: z.boolean(),
  seq: z.number().int().min(0),
});

const heroSchema = z.enum(HERO_IDS as [HeroId, ...HeroId[]]);
const code = z.string().min(3).max(12).regex(/^[A-Z0-9]+$/);

// ------------------------------------------------------------------ admin (owner-only debug console)

const eid = z.number().int().min(0);
const statMod = z.object({ stat: z.string().max(24), add: z.number().optional(), inc: z.number().optional(), more: z.number().optional() });

export const AdminCmdSchema = z.discriminatedUnion('k', [
  z.object({ k: z.literal('god'), on: z.boolean(), target: eid.optional() }),
  z.object({ k: z.literal('heal'), target: eid.optional() }),
  z.object({ k: z.literal('level'), level: z.number().int().min(1).max(20), target: eid.optional() }),
  z.object({ k: z.literal('xp'), amount: z.number().min(0).max(1e6), target: eid.optional() }),
  z.object({ k: z.literal('rank'), rank: z.number().int().min(1).max(20), target: eid.optional() }),
  z.object({ k: z.literal('skill'), id: z.string().max(40), level: z.number().int().min(1).max(5).optional(), aspect: z.string().max(40).optional(), target: eid.optional() }),
  z.object({ k: z.literal('keystone'), id: z.string().max(40), target: eid.optional() }),
  z.object({ k: z.literal('item'), def: z.string().max(40), rarity: z.number().int().min(0).max(3).optional(), target: eid.optional() }),
  z.object({ k: z.literal('consumable'), id: z.string().max(40), n: z.number().int().min(1).max(5).optional(), target: eid.optional() }),
  z.object({ k: z.literal('mods'), mods: z.array(statMod).max(12), target: eid.optional() }),
  z.object({ k: z.literal('teleport'), x: z.number(), y: z.number(), target: eid.optional() }),
  z.object({ k: z.literal('slay'), target: eid }),
  z.object({ k: z.literal('kill'), radius: z.number().min(0).max(6000).optional() }),
  z.object({ k: z.literal('clear') }),
  z.object({ k: z.literal('spawn'), def: z.string().max(40), count: z.number().int().min(1).max(40), elite: z.boolean().optional() }),
  z.object({ k: z.literal('boss') }),
  z.object({ k: z.literal('corruption'), value: z.number().min(0).max(100) }),
  z.object({ k: z.literal('say'), as: eid, text: z.string().min(1).max(200) }),
  z.object({ k: z.literal('announce'), text: z.string().min(1).max(120) }),
  z.object({ k: z.literal('players') }),
  z.object({ k: z.literal('fame'), amount: z.number().int().min(-1e6).max(1e6) }),
  z.object({ k: z.literal('unlock'), hero: z.string().max(20) }),
  z.object({ k: z.literal('slots'), n: z.number().int().min(2).max(6) }),
  z.object({ k: z.literal('pet'), id: z.string().max(24), level: z.number().int().min(1).max(10) }),
  z.object({ k: z.literal('kick'), target: eid }),
]);
export type AdminCmd = z.infer<typeof AdminCmdSchema>;

export interface AdminPlayer {
  id: number;
  accountId: string;
  name: string;
  hero: HeroId;
  level: number;
  rank: number;
  hp: number;
  maxHp: number;
  x: number;
  y: number;
  god: boolean;
  dead: boolean;
}

export interface AdminData {
  players?: AdminPlayer[];
  realm?: { code: string; corruption: number; phase: string; mobs: number; boss: string };
  teleport?: { x: number; y: number };
  /** the account changed: the client should re-read it */
  account?: boolean;
}

const sid = z.string().max(40);
const itemSlot = z.enum(['weapon', 'armor', 'charm']);
const kitOps = [
  z.object({ op: z.literal('unlock'), id: sid }),
  z.object({ op: z.literal('aspect'), id: sid, aspect: sid }),
  z.object({ op: z.literal('trait'), id: sid }),
  z.object({ op: z.literal('respec') }),
  z.object({ op: z.literal('auto'), id: sid, on: z.boolean() }),
] as const;

export const BuildOpSchema: z.ZodType<BuildOp> = z.discriminatedUnion('op', [
  ...kitOps,
  z.object({ op: z.literal('equip'), uid: z.number().int().min(0) }),
  z.object({ op: z.literal('unequip'), slot: itemSlot }),
  z.object({ op: z.literal('drop'), uid: z.number().int().min(0) }),
]);

/** Build changes on a hero resting in a slot: the saved backpack is addressed by index. */
export const SaveOpSchema: z.ZodType<SaveOp> = z.discriminatedUnion('op', [
  ...kitOps,
  z.object({ op: z.literal('equip'), index: z.number().int().min(0).max(15) }),
  z.object({ op: z.literal('unequip'), slot: itemSlot }),
]);

export const C2SSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('hello'), token: z.string().min(8).max(128), version: z.number().int() }),
  z.object({ t: z.literal('join'), mode: z.enum(['realm', 'duel']), code: code.optional(), heroId: heroSchema, ghost: z.string().max(40).optional(), slot: z.number().int().min(0).max(9).optional() }),
  z.object({ t: z.literal('input'), i: InputSchema }),
  z.object({ t: z.literal('respawn'), heroId: heroSchema }),
  z.object({ t: z.literal('chat'), text: z.string().min(1).max(200) }),
  z.object({ t: z.literal('leave') }),
  z.object({ t: z.literal('leaveCancel') }),
  z.object({ t: z.literal('build'), op: z.lazy(() => BuildOpSchema) }),
  z.object({ t: z.literal('watch'), name: z.string().min(2).max(16) }),
  z.object({ t: z.literal('unwatch') }),
  z.object({ t: z.literal('crowd'), id: z.string().max(40) }),
  z.object({ t: z.literal('squad.create') }),
  z.object({ t: z.literal('squad.join'), code }),
  z.object({ t: z.literal('squad.leave') }),
  z.object({ t: z.literal('squad.hero'), heroId: heroSchema, slot: z.number().int().min(0).max(9).optional() }),
  z.object({ t: z.literal('admin.login'), password: z.string().max(128) }),
  z.object({ t: z.literal('admin'), cmd: z.lazy(() => AdminCmdSchema) }),
  z.object({ t: z.literal('squad.start'), mode: z.enum(['realm', 'duel']) }),
  z.object({ t: z.literal('duel.queue') }),
  z.object({ t: z.literal('duel.cancel') }),
  z.object({ t: z.literal('ping'), n: z.number().int() }),
]);

export type C2S = z.infer<typeof C2SSchema>;

export interface MapDescriptor {
  kind: 'realm' | 'arena';
  seed: number;
  w: number;
  h: number;
}

export interface SquadMember {
  accountId: string;
  name: string;
  heroId: HeroId;
  avatar: number;
  leader: boolean;
  inGame: boolean;
  /** level of the saved hero the member brings, if any */
  level?: number;
}

export interface DeathCard {
  ghostId: string;
  name: string;
  heroId: HeroId;
  level: number;
  kills: number;
  bossKills: number;
  revenantKills: number;
  damageDealt: number;
  survivedSec: number;
  killer: { name: string; kind: string };
  build: { actives: { id: string; level: number; aspects: string[] }[]; autos: { id: string; level: number; aspects: string[] }[]; traits: { id: string; level: number }[]; keystones: string[]; weapon: string };
  fame: number;
  realmCode: string;
  rank: number;
  /** the crowd had a hand in this death */
  martyr?: boolean;
}

/** Who a spectator is following. */
export interface SpectateTarget {
  id: number;
  name: string;
  heroId: HeroId;
  accountId: string;
  /** fame prices are shown only when the hero opted into show mode */
  showMode: boolean;
}

export type S2C =
  | { t: 'admin'; ok: boolean; msg?: string; enabled?: boolean; data?: AdminData }
  | { t: 'welcome'; id: number; accountId: string; tick: number; mode: 'realm' | 'duel'; map: MapDescriptor; code: string; team: number; tps: number; spectate?: SpectateTarget }
  | { t: 'spectate'; target: SpectateTarget | null; reason?: string }
  | { t: 'build'; ok: boolean; err?: string }
  | { t: 'leaving'; sec: number; cancelled?: boolean; reason?: string }
  | { t: 'crowd'; ok: boolean; err?: string; fame?: number; prices?: Record<string, number> }
  | { t: 'snap'; s: Snapshot }
  | { t: 'ev'; e: SimEvent[] }
  | { t: 'chat'; from: string; text: string; system?: boolean; id?: number; spectator?: boolean }
  | { t: 'realm'; phase: RealmPhase; corruption: number; players: number; boss: string; closeIn: number; nextCode?: string }
  | { t: 'duel'; phase: DuelPhase; round: number; wins: number[]; names: string[]; until: number; winner?: string }
  | { t: 'death'; card: DeathCard }
  | { t: 'squad'; code: string; members: SquadMember[] }
  | { t: 'squad.none' }
  | { t: 'queue'; state: 'searching' | 'found' | 'cancelled' }
  | { t: 'left' }
  | { t: 'error'; key: string; msg?: string }
  | { t: 'kicked'; reason: string }
  | { t: 'notice'; key: string; vars?: Record<string, string | number> }
  | { t: 'pong'; n: number; tick: number };

export type { InputState, GhostSnapshot };
